import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Download, CreditCard } from "lucide-react";
import { PaymentFilters, PaymentCard } from "@/components/PaymentSections";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ student_id: "", course_id: "", amount: 120, due_date: "", notes: "" });
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(async () => {
    const [p, s, c] = await Promise.all([
      api.get("/payments", { params: { status: filterStatus === "all" ? undefined : filterStatus } }),
      api.get("/students"),
      api.get("/courses"),
    ]);
    setPayments(p.data);
    setStudents(s.data);
    setCourses(c.data);
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const getStudentName = (id) => students.find((s) => s.id === id)?.name || "Unknown";
  const getStudentEmail = (id) => students.find((s) => s.id === id)?.email || "";

  const openCreate = () => {
    setEditing(null);
    const due = new Date();
    due.setDate(due.getDate() + 30);
    setForm({ student_id: students[0]?.id || "", course_id: courses[0]?.id || "", amount: 120, due_date: due.toISOString().split("T")[0], notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ student_id: p.student_id, course_id: p.course_id, amount: p.amount, due_date: p.due_date || "", notes: p.notes || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/payments/${editing.id}`, { amount: form.amount, due_date: form.due_date, notes: form.notes });
        toast.success("Payment updated");
      } else {
        await api.post("/payments", form);
        toast.success("Payment created");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markPaid = async (id) => { try { await api.post(`/payments/${id}/mark-paid`); toast.success("Marked as paid"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const generateInvoice = async (id) => { try { await api.post(`/payments/${id}/generate-invoice`); toast.success("Invoice generated"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const sendReminder = async (id) => { try { const { data } = await api.post(`/payments/${id}/send-reminder`); toast.info(data.message || "Reminder sent"); } catch (e) { toast.error(formatApiError(e)); } };
  const createNextCycle = async (id) => { try { await api.post(`/payments/${id}/create-next-cycle`); toast.success("Next payment cycle created"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const deletePayment = async (payment) => {
    if (!window.confirm(`Delete payment of €${Number(payment.amount).toFixed(2)} for ${getStudentName(payment.student_id)}?`)) return;
    try { await api.delete(`/payments/${payment.id}`); toast.success("Payment deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-payments" className="space-y-6 animate-fade-in-up">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Payments</h1>
            <p className="text-[#2B2B2B]/50 mt-1 text-sm">Track session payments and invoices</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="export-csv-button" variant="outline" onClick={() => window.open(`${API_BASE}/api/payments/export/csv`, "_blank")} className="border-[#EADBC8] text-[#5C3A2E] rounded-full hover:bg-[#EADBC8]/30">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button data-testid="create-payment-button" onClick={openCreate} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
              <Plus className="w-4 h-4 mr-1.5" /> New Payment
            </Button>
          </div>
        </div>

        <PaymentFilters filterStatus={filterStatus} setFilterStatus={setFilterStatus} />

        <div className="grid gap-4">
          {payments.map((p) => (
            <PaymentCard
              key={p.id}
              payment={p}
              getStudentName={getStudentName}
              getStudentEmail={getStudentEmail}
              onMarkPaid={markPaid}
              onGenerateInvoice={generateInvoice}
              onSendReminder={sendReminder}
              onCreateNextCycle={createNextCycle}
              onEdit={openEdit}
              onDelete={deletePayment}
            />
          ))}
          {payments.length === 0 && (
            <div className="text-center py-12">
              <CreditCard className="w-10 h-10 text-[#EADBC8] mx-auto mb-3" />
              <p className="text-[#2B2B2B]/40 text-sm">No payments yet. Create your first payment record.</p>
            </div>
          )}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">{editing ? "Edit Payment" : "New Payment"}</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">{editing ? "Update payment details" : "Create a payment for a 4-session cycle"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {!editing && (
                <>
                  <div>
                    <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Student</label>
                    <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                      <SelectTrigger data-testid="payment-student-select" className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select student" /></SelectTrigger>
                      <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Course</label>
                    <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                      <SelectTrigger data-testid="payment-course-select" className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select course" /></SelectTrigger>
                      <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Amount (&euro;)</label>
                <Input data-testid="payment-amount-input" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Due Date</label>
                <Input data-testid="payment-due-date-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Notes (optional)</label>
                <Textarea data-testid="payment-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-xl border-[#EADBC8] resize-none" />
              </div>
              <Button data-testid="save-payment-button" onClick={handleSave} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                {editing ? "Save Changes" : "Create Payment"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
